/* eslint-disable jsdoc/require-jsdoc -- 私有解析步骤由公开解析入口与行为测试统一说明。 */

import type {
  FulfillmentOrderResourceSelection,
  FulfillmentResourceMentionCandidate,
  FulfillmentTransportPoint,
} from "../types";

export const CONFIRMATION_ENTRY_RESOURCE_TYPES = [
  "住宿",
  "大交通",
  "包车",
  "考察点",
  "餐",
  "导游",
  "杂费",
] as const;

export type ConfirmationEntryResourceType = typeof CONFIRMATION_ENTRY_RESOURCE_TYPES[number];

/** 页面快速录入入口；杂费保留为兼容数据类型，但不再提供新增入口。 */
export const VISIBLE_CONFIRMATION_ENTRY_RESOURCE_TYPES = [
  "住宿",
  "大交通",
  "包车",
  "考察点",
  "餐",
  "导游",
] as const satisfies readonly ConfirmationEntryResourceType[];

export const CONFIRMATION_LINKED_RESOURCE_TYPES = ["住宿", "大交通", "考察点"] as const;

export type ConfirmationLinkedResourceType = typeof CONFIRMATION_LINKED_RESOURCE_TYPES[number];

export type ConfirmationResourceMentionCandidate = FulfillmentResourceMentionCandidate;

export type ConfirmationItineraryDay = {
  dayNo: number;
  date: string;
};

export type ConfirmationTransportMode = "air" | "rail";

export type ConfirmationEntry = {
  resourceType: ConfirmationEntryResourceType;
  spuTableName: string | null;
  spuId: string | null;
  skuTableName: string | null;
  skuId: string | null;
  resourceSelection?: FulfillmentOrderResourceSelection | null;
  itemName: string;
  spec: string | null;
  travelerRole: string | null;
  mealType: string | null;
  dayExpression: string;
  dayNumbers: number[];
  serviceDates: string[];
  outOfRangeDayNumbers: number[];
  needsDateAdjustment: boolean;
  quantity: string;
  billingCount: string;
  unit: string;
  unitPrice: string;
  amount: string;
  sourceText: string;
  transportMode?: ConfirmationTransportMode | null;
  transportFromPointName?: string | null;
  transportToPointName?: string | null;
  transportSeatClass?: string | null;
};

export type ParseConfirmationEntriesResult =
  | { ok: true; entries: ConfirmationEntry[] }
  | { ok: false; error: string; segmentIndex: number };

type ParseInput = {
  resourceType: ConfirmationEntryResourceType;
  text: string;
  itineraryDays?: readonly ConfirmationItineraryDay[];
  mentionCandidates?: readonly ConfirmationResourceMentionCandidate[];
  selectedStaySpu?: ConfirmationResourceMentionCandidate | null;
  transportResourceCandidates?: readonly ConfirmationTransportResourceCandidate[];
  transportPoints?: readonly (FulfillmentTransportPoint & { mode: ConfirmationTransportMode })[];
  requireLinkedMentions?: boolean;
};

type ParsedDay = {
  expression: string;
  numbers: number[];
  isRange: boolean;
};

type ParsedCore = Pick<
  ConfirmationEntry,
  "itemName" | "spec" | "travelerRole" | "mealType" | "quantity" | "billingCount" | "unit" | "unitPrice"
> & Pick<
  ConfirmationEntry,
  "transportMode" | "transportFromPointName" | "transportToPointName" | "transportSeatClass"
> & { resourceSelection?: FulfillmentOrderResourceSelection | null };

type ResolvedResourceReference = {
  spuTableName: string | null;
  spuId: string | null;
  spuName: string | null;
  skuTableName: string | null;
  skuId: string | null;
  skuName: string | null;
  travelerRole: string | null;
  resourceSelection?: FulfillmentOrderResourceSelection | null;
};

export type ConfirmationTransportResourceCandidate = ResolvedResourceReference & {
  transportMode: ConfirmationTransportMode;
  fromPointId: string;
  fromPointName: string;
  toPointId: string;
  toPointName: string;
  seatClass: string | null;
};

type MentionMatch = {
  candidate: ConfirmationResourceMentionCandidate;
  rest: string;
};

function takeFreePointSku(content: string): { skuName: string; rest: string } | null {
  const matched = content.match(
    /^#(.+?)\s+(?=(?:(?:学生|老师|教师|导游|司机)\s+)?\d+(?:\.\d+)?(?:\s*[xX×*]|\s*(?:张|辆|人|项|份|桌|次)?\s+|\s*$))/u,
  );
  const skuName = matched?.[1]?.normalize("NFKC").replace(/\s+/gu, " ").trim();
  if (!matched || !skuName) return null;
  return { skuName, rest: content.slice(matched[0].length).trimStart() };
}

const DAY_AT_START = /^(?:(?:D|第)?\s*)?(\d+(?:\s*(?:-|至|到|,|，)\s*\d+)*)(?:\s*天)?(?:\s+|(?=@))/iu;
const EXPLICIT_DAY = /(?:^|\s)(D\s*\d+(?:\s*(?:-|至|到|,|，)\s*(?:D\s*)?\d+)*|第\s*\d+\s*天)(?=\s|$)/iu;
const ROLE_AT_END = /(?:^|\s)(学生|老师|教师|导游|司机|成人|儿童)$/u;
const POINT_TRAVELER_ROLE_ALIASES: Readonly<Record<string, string>> = {
  学生: "student",
  老师: "teacher",
  教师: "teacher",
  导游: "guide",
  司机: "driver",
};
const MAX_CONFIRMATION_DAY = 366;
const MAX_MONEY_CENTS = 999_999_999_999_999_999n;

function isLinkedResourceType(
  resourceType: ConfirmationEntryResourceType,
): resourceType is ConfirmationLinkedResourceType {
  return (CONFIRMATION_LINKED_RESOURCE_TYPES as readonly string[]).includes(resourceType);
}

function matchMention(
  content: string,
  candidates: readonly ConfirmationResourceMentionCandidate[],
  level: "spu" | "sku",
  parentSpuId: string | null | undefined,
  marker = "@",
): MentionMatch | null | "ambiguous" {
  const matches = candidates
    .filter((candidate) => candidate.level === level
      && (level === "spu" || parentSpuId === undefined || candidate.spuId === parentSpuId))
    .filter((candidate) => {
      const token = `${marker}${candidate.mentionText}`;
      if (!content.startsWith(token)) return false;
      const next = content.slice(token.length, token.length + 1);
      return !next || /\s/u.test(next) || (level === "sku" && /[\d住]/u.test(next));
    })
    .sort((left, right) => right.mentionText.length - left.mentionText.length);
  if (!matches.length) return null;
  const longestLength = matches[0]!.mentionText.length;
  const longest = matches.filter((candidate) => candidate.mentionText.length === longestLength);
  if (new Set(longest.map((candidate) => `${candidate.spuId}:${candidate.skuId ?? ""}`)).size > 1) {
    return "ambiguous";
  }
  const candidate = longest[0]!;
  return {
    candidate,
    rest: content.slice(candidate.mentionText.length + 1).trimStart(),
  };
}

function resolveResourceMentions(
  resourceType: ConfirmationEntryResourceType,
  content: string,
  candidates: readonly ConfirmationResourceMentionCandidate[],
  inheritedResourceSpu: ConfirmationResourceMentionCandidate | null,
): {
  content: string;
  reference: ResolvedResourceReference | null;
  resourceSpu: ConfirmationResourceMentionCandidate | null;
  transportMode: ConfirmationTransportMode | null;
} | null | "ambiguous_stay" {
  if (!isLinkedResourceType(resourceType)) {
    return {
      content,
      reference: null,
      resourceSpu: null,
      transportMode: null,
    };
  }
  const transportModePrefix = resourceType === "大交通"
    ? content.trim().match(/^(飞机|火车)\s+/u)?.[1] ?? null
    : null;
  const transportMode = transportModePrefix === "飞机"
    ? "air"
    : transportModePrefix === "火车"
      ? "rail"
      : null;
  const scoped = candidates.filter((candidate) => (
    candidate.resourceType === resourceType
    && (!transportMode || candidate.transportMode === transportMode)
  ));
  let rest = transportModePrefix
    ? content.trim().slice(transportModePrefix.length).trimStart()
    : content.trim();
  const startedWithMention = rest.startsWith("@") || rest.startsWith("#");
  let spu = resourceType === "住宿" || resourceType === "考察点"
    ? inheritedResourceSpu
    : null;
  let selectedSpu = false;
  let sku: ConfirmationResourceMentionCandidate | null = null;

  if (resourceType === "大交通" && /^@.+?\s*-\s*@/u.test(rest)) {
    return {
      content: rest,
      reference: null,
      resourceSpu: null,
      transportMode,
    };
  }

  if (resourceType === "考察点" && rest.startsWith("@")) {
    const globalSkuMatch = matchMention(rest, scoped, "sku", undefined);
    if (globalSkuMatch === "ambiguous") return null;
    if (globalSkuMatch && !globalSkuMatch.rest.startsWith("@") && !globalSkuMatch.rest.startsWith("#")) {
      sku = globalSkuMatch.candidate;
      spu = globalSkuMatch.candidate;
      rest = globalSkuMatch.rest;
    }
  }

  if (!sku) {
    const spuMatch = matchMention(rest, scoped, "spu", null);
    if (spuMatch === "ambiguous") return null;
    if (spuMatch) {
      spu = spuMatch.candidate;
      selectedSpu = true;
      rest = spuMatch.rest;
    }
  }

  if (resourceType === "住宿" && !spu && rest.startsWith("@")) {
    const skuMatch = matchMention(rest, scoped, "sku", undefined);
    if (skuMatch === "ambiguous") return "ambiguous_stay";
    if (!skuMatch) return null;
    sku = skuMatch.candidate;
    spu = skuMatch.candidate;
    rest = skuMatch.rest;
  }

  const skuMarker = rest.startsWith("@")
    ? "@"
    : resourceType === "考察点" && rest.startsWith("#")
      ? "#"
      : null;
  if (skuMarker) {
    if (!spu) return null;
    const skuMatch = matchMention(
      rest,
      scoped,
      "sku",
      resourceType === "住宿" ? undefined : spu.spuId,
      skuMarker,
    );
    if (skuMatch === "ambiguous") return null;
    if (skuMatch) {
      sku = skuMatch.candidate;
      rest = skuMatch.rest;
    } else if (resourceType === "考察点" && skuMarker === "#") {
      const freeSku = takeFreePointSku(rest);
      if (!freeSku) return null;
      sku = {
        ...spu,
        level: "sku",
        mentionText: freeSku.skuName,
        skuTableName: null,
        skuId: null,
        skuName: freeSku.skuName,
        travelerRole: null,
      };
      rest = freeSku.rest;
    } else {
      return null;
    }
  }

  if (rest.includes("@") || rest.includes("#")
    || (startedWithMention && !selectedSpu && !sku)) return null;
  if (!selectedSpu && !sku) {
    return { content, reference: null, resourceSpu: spu, transportMode };
  }
  if (!spu || !sku) return null;
  if (!spu.spuTableName || !spu.spuId || !spu.spuName || !sku.skuName) return null;
  const unresolvedPointSku = resourceType === "考察点" && !sku.skuId;
  if (resourceType !== "住宿" && !unresolvedPointSku
    && (!sku.skuTableName || !sku.skuId)) return null;

  const normalizedContent = resourceType === "住宿"
    ? `${sku?.skuName ?? ""}${rest}`.trim()
    : [
        spu.spuName,
        sku?.skuName === spu.spuName && !selectedSpu ? null : sku?.skuName,
        rest,
      ].filter(Boolean).join(" ");
  if (!normalizedContent) return null;
  return {
    content: normalizedContent,
    reference: {
      spuTableName: spu.spuTableName,
      spuId: spu.spuId,
      spuName: spu.spuName,
      skuTableName: resourceType === "住宿" ? null : sku.skuTableName,
      skuId: resourceType === "住宿" ? null : sku.skuId,
      skuName: sku?.skuName ?? null,
      travelerRole: sku?.travelerRole ?? null,
      resourceSelection: resourceType === "住宿"
        ? { kind: "stay", staySpuId: spu.spuId, skuName: sku.skuName }
        : unresolvedPointSku
          ? { kind: "point", pointSpuId: spu.spuId, skuName: sku.skuName }
        : null,
    },
    resourceSpu: resourceType === "住宿" || resourceType === "考察点" ? spu : null,
    transportMode,
  };
}

function decimalParts(value: string): { integer: bigint; scale: number } | null {
  const normalized = value.trim();
  if (!/^\d+(?:\.\d+)?$/u.test(normalized)) return null;
  const [whole, fraction = ""] = normalized.split(".");
  const significantWhole = whole.replace(/^0+(?=\d)/u, "");
  if (significantWhole.length > 16 || fraction.length > 2) return null;
  return {
    integer: BigInt(`${significantWhole}${fraction}`),
    scale: fraction.length,
  };
}

function isSupportedDecimal(value: string, allowZero: boolean): boolean {
  const parsed = decimalParts(value);
  return Boolean(parsed && (allowZero || parsed.integer > 0n));
}

/** 金额按十进制定点字符串相乘，避免浮点累计误差。 */
function multiplyDecimals(...values: string[]): string {
  let integer = 1n;
  let scale = 0;
  for (const value of values) {
    const parsed = decimalParts(value);
    if (!parsed) throw new Error("invalid decimal");
    integer *= parsed.integer;
    scale += parsed.scale;
  }
  const scaleDifference = scale - 2;
  const cents = scaleDifference <= 0
    ? integer * (10n ** BigInt(-scaleDifference))
    : (() => {
        const divisor = 10n ** BigInt(scaleDifference);
        return (integer + divisor / 2n) / divisor;
      })();
  if (cents > MAX_MONEY_CENTS) throw new Error("amount overflow");
  const whole = cents / 100n;
  const fraction = String(cents % 100n).padStart(2, "0").replace(/0+$/u, "");
  return fraction ? `${whole}.${fraction}` : whole.toString();
}

function parseDayToken(raw: string): ParsedDay | null {
  const normalized = raw
    .replace(/^第\s*/u, "")
    .replace(/\s*天$/u, "")
    .replace(/D\s*/giu, "")
    .replace(/[至到]/gu, "-")
    .replace(/，/gu, ",")
    .replace(/\s+/gu, "");
  if (!/^\d+(?:(?:-|,)\d+)*$/u.test(normalized)) return null;

  const range = normalized.match(/^(\d+)-(\d+)$/u);
  if (range) {
    const start = Number(range[1]);
    const end = Number(range[2]);
    if (!Number.isSafeInteger(start) || !Number.isSafeInteger(end)
      || start < 1 || end < start || end > MAX_CONFIRMATION_DAY) return null;
    return {
      expression: `D${start}-D${end}`,
      numbers: Array.from({ length: end - start + 1 }, (_, index) => start + index),
      isRange: true,
    };
  }

  const numbers = [...new Set(normalized.split(",").map(Number))];
  if (numbers.some((day) => !Number.isSafeInteger(day)
    || day < 1 || day > MAX_CONFIRMATION_DAY)) return null;
  return {
    expression: numbers.map((day) => `D${day}`).join(","),
    numbers,
    isRange: false,
  };
}

/** 供确认件编辑抽屉复用录入器的出行天语义，避免两处解析规则漂移。 */
export function parseConfirmationDayExpression(raw: string): {
  expression: string;
  numbers: number[];
} | null {
  const parsed = parseDayToken(raw.trim());
  return parsed ? { expression: parsed.expression, numbers: parsed.numbers } : null;
}

/** 仅识别片段开头的裸日号，或任意位置显式书写的 D/第N天。 */
function takeDay(segment: string): { content: string; day: ParsedDay | null } {
  const explicit = segment.match(EXPLICIT_DAY);
  if (explicit) {
    const day = parseDayToken(explicit[1]);
    if (day) {
      return {
        content: `${segment.slice(0, explicit.index)} ${segment.slice((explicit.index ?? 0) + explicit[0].length)}`.trim(),
        day,
      };
    }
    return { content: "", day: null };
  }
  const atStart = segment.match(DAY_AT_START);
  if (!atStart) return { content: segment.trim(), day: null };
  const day = parseDayToken(atStart[1]);
  return day
    ? { content: segment.slice(atStart[0].length).trim(), day }
    : { content: "", day: null };
}

function parseQuantityAndPrice(content: string): {
  prefix: string;
  quantity: string;
  unit: string;
  unitPrice: string;
} | null {
  const matched = content.match(/^(.*?)\s+(\d+(?:\.\d+)?)\s*(张|辆|人|项|份|桌|次)\s+(\d+(?:\.\d+)?)\s*元?$/u);
  return matched
    && isSupportedDecimal(matched[2], false)
    && isSupportedDecimal(matched[4], true)
    ? { prefix: matched[1].trim(), quantity: matched[2], unit: matched[3], unitPrice: matched[4] }
    : null;
}

function takeRole(prefix: string): { text: string; role: string | null } {
  const matched = prefix.match(ROLE_AT_END);
  if (!matched) return { text: prefix.trim(), role: null };
  return {
    text: prefix.slice(0, matched.index).trim(),
    role: matched[1] === "教师" ? "老师" : matched[1],
  };
}

function splitFirstWord(value: string): { first: string; rest: string | null } {
  const [first = "", ...rest] = value.trim().split(/\s+/u);
  return { first, rest: rest.length ? rest.join(" ") : null };
}

function parseStay(content: string, day: ParsedDay): ParsedCore | null {
  const matched = content.match(/^(.+?)(\d+(?:\.\d+)?)\s*[xX×*]\s*(\d+(?:\.\d+)?)\s*(?:元)?$/u);
  if (!matched
    || !isSupportedDecimal(matched[2], false)
    || !isSupportedDecimal(matched[3], true)) return null;
  const descriptor = matched[1].trim();
  const staySubjectMatched = descriptor.match(/住(两人|学生|老师|教师|导游|司机|成人|儿童)$/u);
  const staySubject = staySubjectMatched?.[1] ?? null;
  const travelerRole = staySubject && staySubject !== "两人"
    ? staySubject === "教师" ? "老师" : staySubject
    : null;
  const billingCount = String(day.isRange
    ? Math.max((day.numbers.at(-1) ?? 1) - day.numbers[0], 1)
    : day.numbers.length);
  return {
    itemName: staySubjectMatched
      ? descriptor.slice(0, staySubjectMatched.index).trim()
      : descriptor,
    spec: staySubject === "两人" ? "两人" : null,
    travelerRole,
    mealType: null,
    quantity: matched[2],
    billingCount,
    unit: "间",
    unitPrice: matched[3],
  };
}

function parseMeal(content: string, day: ParsedDay): ParsedCore | null {
  const matched = content.match(/^(.+?)\s+(\d+(?:\.\d+)?)\s*[xX×*]\s*(\d+(?:\.\d+)?)\s*(?:元)?$/u);
  if (!matched
    || !isSupportedDecimal(matched[2], false)
    || !isSupportedDecimal(matched[3], true)) return null;
  let descriptor = matched[1].trim();
  let billingCount = String(day.numbers.length);
  const countMatched = descriptor.match(/^(\d+)\s*个\s*(.+)$/u);
  if (countMatched) {
    if (!isSupportedDecimal(countMatched[1], false)) return null;
    billingCount = countMatched[1];
    descriptor = countMatched[2].trim();
  }
  const mealMatched = descriptor.match(/^(早餐|中餐|晚餐|正餐)(.*)$/u);
  if (!mealMatched) return null;
  const spec = mealMatched[2].trim() || null;
  return {
    itemName: mealMatched[1],
    spec,
    travelerRole: null,
    mealType: mealMatched[1],
    quantity: matched[2],
    billingCount,
    unit: spec?.includes("桌") ? "桌" : "人",
    unitPrice: matched[3],
  };
}

type MealParseContext = {
  location: string | null;
  travelerRole: string | null;
  mealType: string | null;
};

function parseMealBatch(
  content: string,
  day: ParsedDay,
  inherited: MealParseContext,
): { cores: ParsedCore[]; context: MealParseContext } | null {
  const legacy = parseMeal(content, day);
  if (legacy) {
    return { cores: [legacy], context: { ...inherited, mealType: legacy.mealType } };
  }

  const parts = content.split(/[，,]/u).map((part) => part.trim()).filter(Boolean);
  if (!parts.length) return null;
  const context = { ...inherited };
  const cores: ParsedCore[] = [];
  for (const part of parts) {
    let rest = part;
    const location = rest.match(/^@([^\s,，;；]+)\s*/u);
    if (location) {
      context.location = location[1].trim();
      rest = rest.slice(location[0].length);
    }
    const role = rest.match(/^(学生|老师|教师|导游|司机|成人|儿童)\s*/u);
    if (role) {
      context.travelerRole = role[1] === "教师" ? "老师" : role[1];
      rest = rest.slice(role[0].length);
    }
    const meal = rest.match(/^(早餐|中餐|晚餐|正餐)\s*/u);
    if (meal) {
      context.mealType = meal[1];
      rest = rest.slice(meal[0].length);
    }
    if (!context.mealType) return null;

    const triple = rest.match(/^(\d+(?:\.\d+)?)\s*[xX×*]\s*(\d+(?:\.\d+)?)\s*[xX×*]\s*(\d+(?:\.\d+)?)\s*元?$/u);
    const pair = triple
      ? null
      : rest.match(/^(\d+(?:\.\d+)?)\s*[xX×*]\s*(\d+(?:\.\d+)?)\s*元?$/u);
    const quantity = triple?.[1] ?? pair?.[1];
    const billingCount = triple?.[2] ?? (pair ? "1" : undefined);
    const unitPrice = triple?.[3] ?? pair?.[2];
    if (!quantity || !billingCount || !unitPrice
      || !isSupportedDecimal(quantity, false)
      || !isSupportedDecimal(billingCount, false)
      || !isSupportedDecimal(unitPrice, true)) return null;
    cores.push({
      itemName: context.mealType,
      spec: context.location,
      travelerRole: context.travelerRole,
      mealType: context.mealType,
      quantity,
      billingCount,
      unit: "人",
      unitPrice,
    });
  }
  return { cores, context };
}

function parseGuideBatch(content: string, day: ParsedDay): ParsedCore[] | null {
  if (!content.trim().startsWith("@")) return null;
  const parts = content
    .split(/[，,]|\s*(?=@)/u)
    .map((part) => part.trim())
    .filter(Boolean);
  if (!parts.length) return null;
  const cores = parts.map((part): ParsedCore | null => {
    const matched = part.match(/^@(.+?)\s+(\d+(?:\.\d+)?)\s*元?$/u);
    if (!matched || !matched[1].trim() || !isSupportedDecimal(matched[2], true)) return null;
    return {
      itemName: matched[1].trim(),
      spec: null,
      travelerRole: null,
      mealType: null,
      quantity: "1",
      billingCount: String(day.numbers.length),
      unit: "人",
      unitPrice: matched[2],
    };
  });
  return cores.every(Boolean) ? cores as ParsedCore[] : null;
}

function parseTransportEndpoint(
  content: string,
  inheritedMode: ConfirmationTransportMode | null,
): ParsedCore | null {
  const matched = content.match(
    /^(.*?)\s+(\d+(?:\.\d+)?)\s*(?:(?:[xX×*]\s*)|张\s*)(\d+(?:\.\d+)?)\s*元?$/u,
  );
  if (!matched
    || !isSupportedDecimal(matched[2], false)
    || !isSupportedDecimal(matched[3], true)) return null;
  const route = matched[1].trim().match(
    /^(?:(飞机|火车)\s+)?@(.+?)\s*-\s*@(.+?)(?:\s*#\s*(.+))?$/u,
  );
  if (!route) return null;
  const mode = route[1] === "飞机"
    ? "air"
    : route[1] === "火车"
      ? "rail"
      : inheritedMode
        ?? (route[2].includes("机场") && route[3].includes("机场") ? "air" : null)
        ?? (route[2].endsWith("站") && route[3].endsWith("站") ? "rail" : null);
  if (!mode) return null;
  const from = route[2].trim();
  const to = route[3].trim();
  const seatClass = route[4]?.trim() || null;
  return {
    itemName: `${from}-${to}`,
    spec: seatClass,
    travelerRole: null,
    mealType: null,
    quantity: matched[2],
    billingCount: "1",
    unit: "张",
    unitPrice: matched[3],
    transportMode: mode,
    transportFromPointName: from,
    transportToPointName: to,
    transportSeatClass: seatClass,
  };
}

function matchTransportResource(
  core: ParsedCore,
  candidates: readonly ConfirmationTransportResourceCandidate[],
): ResolvedResourceReference | null | "ambiguous" {
  const matches = candidates.filter((candidate) => (
    candidate.transportMode === core.transportMode
    && candidate.fromPointName === core.transportFromPointName
    && candidate.toPointName === core.transportToPointName
    && (!core.transportSeatClass || candidate.seatClass === core.transportSeatClass)
  ));
  if (matches.length !== 1) return matches.length > 1 ? "ambiguous" : null;
  const [matched] = matches;
  return {
    spuTableName: matched.spuTableName,
    spuId: matched.spuId,
    spuName: matched.spuName,
    skuTableName: matched.skuTableName,
    skuId: matched.skuId,
    skuName: matched.skuName,
    travelerRole: matched.travelerRole,
  };
}

type BusEntrySemantic = Extract<FulfillmentOrderResourceSelection, { kind: "bus" }>["entrySemantic"];

function busEntrySemantic(content: string): BusEntrySemantic | null {
  if (content.includes("单接")) return "pickup";
  if (content.includes("单送")) return "dropoff";
  if (content.includes("包车")) return "local_charter";
  if (content.includes("接送")) return "transfer";
  return null;
}

function busEntryName(semantic: BusEntrySemantic): string {
  if (semantic === "pickup") return "单接";
  if (semantic === "dropoff") return "单送";
  return "当地用车";
}

function resolveVehicleSpec(
  rawSpec: string,
  candidates: readonly ConfirmationResourceMentionCandidate[],
  requireCandidate = false,
): string | null {
  const normalizeSeatSpec = (value: string): string | null => {
    const matched = value.normalize("NFKC").replace(/^@/u, "").trim().match(/^([1-9]\d{0,2})\s*座$/u);
    return matched ? `${Number(matched[1])}座` : null;
  };
  const normalized = normalizeSeatSpec(rawSpec);
  if (!normalized) return null;
  const available = candidates
    .filter((candidate) => candidate.resourceType === "包车" && candidate.level === "sku")
    .map((candidate) => candidate.vehicleSpecName ?? candidate.skuName ?? candidate.mentionText)
    .map(normalizeSeatSpec)
    .filter((value): value is string => Boolean(value));
  if (available.length === 0) return requireCandidate ? null : normalized;
  return available.find((value) => value === normalized) ?? null;
}

function parseCharterBatch(
  content: string,
  candidates: readonly ConfirmationResourceMentionCandidate[],
  requireCandidate = false,
): ParsedCore[] | null {
  const matched = content.match(/^(?:(接送|单接|单送|包车)\s+)?(.+)$/u);
  if (!matched) return null;
  const semantic = matched[1] ? busEntrySemantic(matched[1]) : "local_charter";
  if (!semantic) return null;
  const entries = matched[2]
    .split(/[，,]/u)
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => item.match(/^(@?\S+)\s*(\d+(?:\.\d+)?)\s*[xX×*]\s*(\d+(?:\.\d+)?)\s*元?$/u))
    .map((item) => {
      const vehicleSpecName = item
        ? resolveVehicleSpec(item[1], candidates, requireCandidate)
        : null;
      if (!item || !vehicleSpecName
        || !isSupportedDecimal(item[2], false) || !isSupportedDecimal(item[3], true)) {
        return null;
      }
      return {
        itemName: busEntryName(semantic),
        spec: vehicleSpecName,
        travelerRole: null,
        mealType: null,
        quantity: item[2],
        billingCount: "1",
        unit: "辆",
        unitPrice: item[3],
        resourceSelection: {
          kind: "bus",
          vehicleSpecName,
          entrySemantic: semantic,
        },
      } satisfies ParsedCore;
    });
  return entries.length && entries.every(Boolean)
    ? entries as ParsedCore[]
    : null;
}

function parseTransportLike(
  resourceType: Exclude<ConfirmationEntryResourceType, "住宿" | "餐">,
  content: string,
  day: ParsedDay,
  mentionCandidates: readonly ConfirmationResourceMentionCandidate[] = [],
  requireBusCandidate = false,
): ParsedCore | null {
  if (resourceType === "大交通") {
    const endpoint = parseTransportEndpoint(content, null);
    if (endpoint) return endpoint;
  }
  const quantified = parseQuantityAndPrice(content);
  if (!quantified) return null;
  const withRole = takeRole(quantified.prefix);
  let itemName = withRole.text;
  let spec: string | null = null;

  if (resourceType === "大交通" || resourceType === "考察点") {
    const split = splitFirstWord(withRole.text);
    itemName = split.first;
    spec = split.rest;
  } else if (resourceType === "包车") {
    const specOnly = withRole.text.match(/^(@?\S+)$/u);
    const specMatched = specOnly
      ? [withRole.text, "", specOnly[1]]
      : withRole.text.match(/^(.*?)\s+(@?\S+)$/u);
    if (specMatched) {
      const semantic = specOnly ? "local_charter" : busEntrySemantic(specMatched[1]);
      const vehicleSpecName = resolveVehicleSpec(
        specMatched[2],
        mentionCandidates,
        requireBusCandidate,
      );
      if (!semantic || !vehicleSpecName) return null;
      itemName = busEntryName(semantic);
      spec = vehicleSpecName;
      return {
        itemName,
        spec,
        travelerRole: withRole.role,
        mealType: null,
        quantity: quantified.quantity,
        billingCount: "1",
        unit: quantified.unit,
        unitPrice: quantified.unitPrice,
        resourceSelection: {
          kind: "bus",
          vehicleSpecName,
          entrySemantic: semantic,
        },
      };
    }
  }
  if (!itemName) return null;
  return {
    itemName,
    spec,
    travelerRole: withRole.role,
    mealType: null,
    quantity: quantified.quantity,
    billingCount: resourceType === "导游" ? String(day.numbers.length) : "1",
    unit: quantified.unit,
    unitPrice: quantified.unitPrice,
  };
}

function parsePoint(content: string, day: ParsedDay): ParsedCore | null {
  const matched = content.match(
    /^(.*?)\s+(\d+(?:\.\d+)?)\s*[xX×*]\s*(\d+(?:\.\d+)?)\s*[xX×*]\s*(\d+(?:\.\d+)?)\s*元?$/u,
  );
  if (!matched) {
    const shorthand = content.match(
      /^(.*?)\s+(学生|老师|教师|导游|司机)\s+(\d+(?:\.\d+)?)\s*元?$/u,
    );
    if (shorthand && isSupportedDecimal(shorthand[3], true)) {
      const split = splitFirstWord(shorthand[1].trim());
      if (split.first && split.rest) {
        return {
          itemName: split.first,
          spec: split.rest,
          travelerRole: shorthand[2] === "教师" ? "老师" : shorthand[2],
          mealType: null,
          quantity: "1",
          billingCount: "1",
          unit: "人",
          unitPrice: shorthand[3],
        };
      }
    }
    const pair = content.match(
      /^(.*?)\s+(\d+(?:\.\d+)?)\s*[xX×*]\s*(\d+(?:\.\d+)?)\s*元?$/u,
    );
    if (!pair
      || !isSupportedDecimal(pair[2], false)
      || !isSupportedDecimal(pair[3], true)) return parseTransportLike("考察点", content, day);
    const withRole = takeRole(pair[1].trim());
    const split = splitFirstWord(withRole.text);
    if (!split.first) return null;
    return {
      itemName: split.first,
      spec: split.rest,
      travelerRole: withRole.role,
      mealType: null,
      quantity: pair[2],
      billingCount: "1",
      unit: "人",
      unitPrice: pair[3],
    };
  }
  if (!isSupportedDecimal(matched[2], false)
    || !isSupportedDecimal(matched[3], false)
    || !isSupportedDecimal(matched[4], true)) return null;
  const withRole = takeRole(matched[1].trim());
  const split = splitFirstWord(withRole.text);
  if (!split.first || !split.rest) return null;
  return {
    itemName: split.first,
    spec: split.rest,
    travelerRole: withRole.role,
    mealType: null,
    quantity: matched[2],
    billingCount: matched[3],
    unit: "人",
    unitPrice: matched[4],
  };
}

function buildEntry(
  resourceType: ConfirmationEntryResourceType,
  core: ParsedCore,
  day: ParsedDay,
  itineraryDays: readonly ConfirmationItineraryDay[],
  sourceText: string,
  reference: ResolvedResourceReference | null,
): ConfirmationEntry {
  const dateByDay = new Map(itineraryDays.map((item) => [item.dayNo, item.date]));
  const serviceDates = day.numbers.flatMap((dayNo) => {
    const date = dateByDay.get(dayNo);
    return date ? [date] : [];
  });
  const outOfRangeDayNumbers = itineraryDays.length
    ? day.numbers.filter((dayNo) => !dateByDay.has(dayNo))
    : [];
  const resourceSelection = core.resourceSelection ?? reference?.resourceSelection ?? null;
  const defersResourceResolution = resourceSelection?.kind === "stay"
    || resourceSelection?.kind === "bus"
    || resourceSelection?.kind === "point";
  return {
    resourceType,
    ...core,
    ...(reference && resourceType === "住宿"
      ? {
          itemName: reference.spuName,
          spec: [reference.skuName ?? core.itemName, core.spec].filter(Boolean).join(" · ") || null,
        }
      : reference
        ? { itemName: reference.spuName, spec: reference.skuName ?? core.spec }
        : {}),
    spuTableName: defersResourceResolution ? null : reference?.spuTableName ?? null,
    spuId: defersResourceResolution ? null : reference?.spuId ?? null,
    skuTableName: defersResourceResolution ? null : reference?.skuTableName ?? null,
    skuId: defersResourceResolution ? null : reference?.skuId ?? null,
    resourceSelection,
    dayExpression: day.expression,
    dayNumbers: day.numbers,
    serviceDates,
    outOfRangeDayNumbers,
    needsDateAdjustment: outOfRangeDayNumbers.length > 0,
    amount: multiplyDecimals(core.quantity, core.billingCount, core.unitPrice),
    sourceText,
  };
}

/** 按确认件原型语法原子解析；任一片段失败时不返回部分明细。 */
export function parseConfirmationEntries({
  resourceType,
  text,
  itineraryDays = [],
  mentionCandidates = [],
  selectedStaySpu = null,
  transportResourceCandidates = [],
  transportPoints,
  requireLinkedMentions = false,
}: ParseInput): ParseConfirmationEntriesResult {
  const normalizedText = resourceType === "考察点"
    ? text.replace(/，(?=\s*#)/gu, ";")
    : text;
  const segments = normalizedText
    .replace(/[；]/gu, ";")
    .split(/[;\r\n]+/u)
    .map((segment) => segment.trim())
    .filter(Boolean);
  if (segments.length === 0) {
    return { ok: false, error: "请输入需要加入的确认件明细", segmentIndex: 1 };
  }

  const entries: ConfirmationEntry[] = [];
  let inheritedDay = parseDayToken("1") as ParsedDay;
  let inheritedResourceSpu: ConfirmationResourceMentionCandidate | null =
    resourceType === "住宿" ? selectedStaySpu : null;
  let inheritedTransportMode: ConfirmationTransportMode | null = null;
  let inheritedMealContext: MealParseContext = {
    location: null,
    travelerRole: null,
    mealType: null,
  };
  for (let index = 0; index < segments.length; index += 1) {
    const sourceText = segments[index];
    const extracted = takeDay(sourceText);
    if (extracted.day) inheritedDay = extracted.day;
    const resolved = resolveResourceMentions(
      resourceType,
      extracted.content,
      mentionCandidates,
      inheritedResourceSpu,
    );
    if (resolved === "ambiguous_stay") {
      return {
        ok: false,
        error: `第 ${index + 1} 个片段的同名房型属于多个酒店，请选择带酒店名称的房型候选`,
        segmentIndex: index + 1,
      };
    }
    if (!resolved) {
      return {
        ok: false,
        error: `第 ${index + 1} 个片段包含未选择或不匹配的 @ 资源，请从候选中重新选择`,
        segmentIndex: index + 1,
      };
    }
    inheritedResourceSpu = resolved.resourceSpu;
    if (resolved.transportMode) inheritedTransportMode = resolved.transportMode;
    const mealBatch = resourceType === "餐"
      ? parseMealBatch(resolved.content, inheritedDay, inheritedMealContext)
      : null;
    if (mealBatch) inheritedMealContext = mealBatch.context;
    const cores = resourceType === "包车"
      ? parseCharterBatch(resolved.content, mentionCandidates, requireLinkedMentions)
        ?? [parseTransportLike(
          resourceType,
          resolved.content,
          inheritedDay,
          mentionCandidates,
          requireLinkedMentions,
        )]
      : resourceType === "导游"
        ? parseGuideBatch(resolved.content, inheritedDay)
          ?? [parseTransportLike(resourceType, resolved.content, inheritedDay)]
        : resourceType === "餐"
          ? mealBatch?.cores ?? [null]
          : [resourceType === "住宿"
          ? parseStay(resolved.content, inheritedDay)
          : resourceType === "大交通"
              ? parseTransportEndpoint(resolved.content, inheritedTransportMode)
                ?? parseTransportLike(resourceType, resolved.content, inheritedDay)
              : resourceType === "考察点"
                ? parsePoint(resolved.content, inheritedDay)
                : parseTransportLike(resourceType, resolved.content, inheritedDay)];
    if (cores.some((core) => !core)) {
      return {
        ok: false,
        error: `第 ${index + 1} 个片段无法识别，请按当前分类示例检查后重试`,
        segmentIndex: index + 1,
      };
    }
    for (const core of cores as ParsedCore[]) {
      if (resourceType === "大交通" && core.transportMode && core.transportFromPointName
        && transportPoints !== undefined) {
        const endpoints = [core.transportFromPointName, core.transportToPointName].map((name) => (
          [...new Map(transportPoints.filter((point) => point.mode === core.transportMode
            && (point.name === name || point.shortName === name)).map((point) => [point.id, point])).values()]
        ));
        const missing = endpoints.findIndex((points) => points.length !== 1);
        if (missing >= 0) {
          const name = missing === 0 ? core.transportFromPointName : core.transportToPointName;
          return { ok: false, segmentIndex: index + 1,
            error: endpoints[missing].length > 1
              ? `“${name}”存在多个同名交通点，请从候选中明确选择`
              : `没有“${name}”${core.transportMode === "air" ? "机场" : "火车站"}，请检查名称或从候选中重新选择`,
          };
        }
        core.resourceSelection = { kind: "transport", transportMode: core.transportMode,
          fromPointId: endpoints[0][0].id, toPointId: endpoints[1][0].id };
      }
      const matchedTransportReference = resourceType === "大交通" && core.transportFromPointName
        && !core.resourceSelection
        ? matchTransportResource(core, transportResourceCandidates)
        : null;
      if (matchedTransportReference === "ambiguous") {
        return {
          ok: false,
          error: `第 ${index + 1} 个片段匹配到多个大交通规格，请补充 #规格后重新选择`,
          segmentIndex: index + 1,
        };
      }
      const inheritedStayReference = resourceType === "住宿"
        && inheritedResourceSpu?.spuId
        && inheritedResourceSpu.spuName
        ? {
            spuTableName: inheritedResourceSpu.spuTableName ?? null,
            spuId: inheritedResourceSpu.spuId,
            spuName: inheritedResourceSpu.spuName,
            skuTableName: null,
            skuId: null,
            skuName: core.itemName,
            travelerRole: core.travelerRole,
            resourceSelection: {
              kind: "stay" as const,
              staySpuId: inheritedResourceSpu.spuId,
              skuName: core.itemName,
            },
          }
        : null;
      const entryReference = matchedTransportReference
        ?? resolved.reference
        ?? inheritedStayReference;
      if (
        requireLinkedMentions
        && isLinkedResourceType(resourceType)
        && (sourceText.includes("@") || sourceText.includes("#"))
        && !entryReference
        && !core.resourceSelection
      ) {
        return {
          ok: false,
          error: resourceType === "大交通"
            ? `第 ${index + 1} 个片段尚未匹配到交通路线及席别，请检查起终点和席别`
            : `第 ${index + 1} 个片段尚未关联完整的 SPU/SKU 资源，请从候选中重新选择`,
          segmentIndex: index + 1,
        };
      }
      if (
        resourceType === "考察点"
        && entryReference?.travelerRole
        && core.travelerRole
        && POINT_TRAVELER_ROLE_ALIASES[core.travelerRole]
          !== entryReference.travelerRole
      ) {
        return {
          ok: false,
          error: `第 ${index + 1} 个片段的旅客角色与所选考察点票种不一致`,
          segmentIndex: index + 1,
        };
      }
      const effectiveCore = resourceType === "考察点"
        && !core.travelerRole
        && entryReference?.travelerRole
        ? {
            ...core,
            travelerRole: Object.entries(POINT_TRAVELER_ROLE_ALIASES)
              .find(([, value]) => value === entryReference.travelerRole)?.[0]
              ?? entryReference.travelerRole,
          }
        : core;
      try {
        entries.push(buildEntry(
          resourceType,
          effectiveCore,
          inheritedDay,
          itineraryDays,
          sourceText,
          entryReference,
        ));
      } catch {
        return {
          ok: false,
          error: `第 ${index + 1} 个片段的数值超出系统可保存范围，请拆分后重试`,
          segmentIndex: index + 1,
        };
      };
    }
  }
  return { ok: true, entries };
}
